import { AddOutlined } from '@mui/icons-material';
import { Button, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import ConfirmationDialog from 'components/v2/common/confirmationDialog';

import ServiceChecklistDialog from './serviceChecklistDialog';
import ServiceFormDialog from './serviceFormDialog';
import ServicesList, { type ServiceRowAction } from './servicesList';
import ListFiltersBar from './listFiltersBar';
import ScopeToolbar from './scopeToolbar';

import api from 'api';
import { ACTIONS } from 'constant';
import { Role } from 'data/constants/roles';
import { useDebouncedValue } from 'hooks/useCheckEmail';
import useModalObj from 'hooks/useModalObj';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type { AppDispatch } from 'redux/store';
import type {
    ActiveStatusFilter,
    OwnershipScope,
    ProviderOption,
    ServiceCountryCode,
    ServiceRecord,
    ServiceScopeFilter,
} from 'types/servicesDocuments';
import { localizedServiceName } from 'utils/servicesDocuments';
import { getErrorMessage } from 'utils/v2/errorHelper';

interface Props {
    userRole: string;
    providerOptions: ProviderOption[];
    providersLoading: boolean;
    fixedProviderId: number | null;
    fixedProviderLabel?: string;
}

const ServicesTabPanel = ({
    userRole,
    providerOptions,
    providersLoading,
    fixedProviderId,
    fixedProviderLabel,
}: Props) => {
    const dispatch = useDispatch<AppDispatch>();
    const { translate, language } = useTranslate();
    const isSuperSuperAdmin = userRole === Role.SUPER_SUPER_ADMIN;

    const [ownershipScope, setOwnershipScope] = useState<OwnershipScope>('provider');
    const [providerId, setProviderId] = useState<number | ''>(fixedProviderId ?? '');
    const [countryCode, setCountryCode] = useState<ServiceCountryCode | ''>('');
    const [statusFilter, setStatusFilter] = useState<ActiveStatusFilter>('all');
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebouncedValue(search, 300);
    const [loading, setLoading] = useState(false);
    const [services, setServices] = useState<ServiceRecord[]>([]);

    const { modalObject, setModalObject, onResetModalObj } = useModalObj<ServiceRecord>();
    const [checklistService, setChecklistService] = useState<ServiceRecord | null>(null);

    useEffect(() => {
        if (!isSuperSuperAdmin && fixedProviderId != null) {
            setProviderId(fixedProviderId);
            setOwnershipScope('provider');
        }
    }, [fixedProviderId, isSuperSuperAdmin]);

    const effectiveProviderId = useMemo(() => {
        if (!isSuperSuperAdmin) {
            return fixedProviderId ?? undefined;
        }
        if (ownershipScope === 'universal') {
            return undefined;
        }
        return providerId === '' ? undefined : Number(providerId);
    }, [fixedProviderId, isSuperSuperAdmin, ownershipScope, providerId]);

    const listScope: ServiceScopeFilter = useMemo(() => {
        if (isSuperSuperAdmin) {
            return ownershipScope === 'universal' ? 'universal' : 'provider';
        }
        return 'visible';
    }, [isSuperSuperAdmin, ownershipScope]);

    const loadServices = useCallback(async () => {
        if (isSuperSuperAdmin && ownershipScope === 'provider' && providerId === '') {
            setServices([]);
            return;
        }
        setLoading(true);
        try {
            const response = await api.services.listServices({
                scope: listScope,
                ...(countryCode ? { country_code: countryCode } : {}),
                ...(effectiveProviderId != null
                    ? { service_provider_id: effectiveProviderId }
                    : {}),
                include_inactive: statusFilter !== 'active',
            });
            let rows = response.data || [];
            if (statusFilter === 'active') {
                rows = rows.filter((row) => row.is_active);
            } else if (statusFilter === 'inactive') {
                rows = rows.filter((row) => !row.is_active);
            }
            const query = debouncedSearch.trim().toLowerCase();
            if (query) {
                rows = rows.filter((row) => {
                    const haystack = [row.name_en, row.name_fr, row.name_es, row.service_code]
                        .filter(Boolean)
                        .join(' ')
                        .toLowerCase();
                    return haystack.includes(query);
                });
            }
            setServices(rows);
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(
                        error,
                        translate('servicesDocuments.services.loadFailed')
                    ),
                    type: 'error',
                })
            );
        } finally {
            setLoading(false);
        }
    }, [
        countryCode,
        debouncedSearch,
        dispatch,
        effectiveProviderId,
        isSuperSuperAdmin,
        listScope,
        ownershipScope,
        providerId,
        statusFilter,
        translate,
    ]);

    useEffect(() => {
        void loadServices();
    }, [loadServices]);

    const handleRowAction = (action: ServiceRowAction, service: ServiceRecord) => {
        if (action === 'CHECKLIST') {
            setChecklistService(service);
            return;
        }
        if (action === ACTIONS.EDIT) {
            setModalObject((prev) => ({
                ...prev,
                action: ACTIONS.EDIT,
                data: service,
                title: 'servicesDocuments.services.editTitle',
            }));
            return;
        }
        setModalObject((prev) => ({
            ...prev,
            action: ACTIONS.DELETE,
            data: service,
            title: 'delete',
            content: localizedServiceName(service, language),
        }));
    };

    const handleDelete = async () => {
        const service = modalObject.data;
        if (!service?.id) {
            return;
        }
        try {
            setModalObject((prev) => ({ ...prev, loading: true }));
            await api.services.deleteService(service.id);
            dispatch(setAlert({ message: translate('saveChanges'), type: 'success' }));
            onResetModalObj();
            await loadServices();
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
            setModalObject((prev) => ({ ...prev, loading: false }));
        }
    };

    const defaultScope = ownershipScope;
    const defaultProviderId = providerId;

    return (
        <Stack spacing={2}>
            <ScopeToolbar
                isSuperSuperAdmin={isSuperSuperAdmin}
                ownershipScope={ownershipScope}
                onOwnershipScopeChange={setOwnershipScope}
                providerId={providerId}
                onProviderIdChange={setProviderId}
                providerOptions={providerOptions}
                providersLoading={providersLoading}
                fixedProviderLabel={fixedProviderLabel}
            />

            <ListFiltersBar
                search={search}
                onSearchChange={setSearch}
                action={
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddOutlined />}
                        onClick={() =>
                            setModalObject((prev) => ({
                                ...prev,
                                action: ACTIONS.CREATE,
                                data: null,
                                title: 'servicesDocuments.services.addTitle',
                            }))
                        }
                        sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}
                    >
                        {translate('servicesDocuments.services.addTitle')}
                    </Button>
                }
            />

            <ServicesList
                loading={loading}
                services={services}
                userRole={userRole}
                currentProviderId={fixedProviderId}
                language={language}
                countryCode={countryCode}
                onCountryChange={setCountryCode}
                statusFilter={statusFilter}
                onStatusChange={setStatusFilter}
                handleAction={handleRowAction}
            />

            {modalObject.action === ACTIONS.CREATE || modalObject.action === ACTIONS.EDIT ? (
                <ServiceFormDialog
                    open
                    onClose={onResetModalObj}
                    onSuccess={loadServices}
                    initialData={modalObject.data}
                    userRole={userRole}
                    defaultScope={defaultScope}
                    defaultProviderId={defaultProviderId}
                    providerOptions={providerOptions}
                />
            ) : null}

            {modalObject.action === ACTIONS.DELETE ? (
                <ConfirmationDialog
                    open
                    title={translate('delete')}
                    content={modalObject.content}
                    loading={modalObject.loading}
                    onClose={onResetModalObj}
                    onConfirm={() => void handleDelete()}
                />
            ) : null}

            <ServiceChecklistDialog
                open={Boolean(checklistService)}
                service={checklistService}
                onClose={() => setChecklistService(null)}
                onSaved={loadServices}
                language={language}
            />
        </Stack>
    );
};

export default ServicesTabPanel;
